import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight, AlertTriangle } from 'lucide-react';

const BEHAVIOURS = [
  { id: 'late', label: 'Replies late', risk: 20 },
  { id: 'contracts', label: 'Avoids contracts', risk: 35 },
  { id: 'history', label: 'History of late payment', risk: 30 },
];

function getRiskLevel(score) {
  if (score >= 70) return { label: 'High Risk', color: '#ef4444', bg: 'bg-red-500', glow: 'shadow-red-500/30' };
  if (score >= 35) return { label: 'Medium Risk', color: '#f59e0b', bg: 'bg-amber-500', glow: 'shadow-amber-500/30' };
  if (score > 0) return { label: 'Low Risk', color: '#22c55e', bg: 'bg-green-500', glow: 'shadow-green-500/30' };
  return { label: 'Select behaviours', color: '#6b7280', bg: 'bg-gray-500', glow: '' };
}

export default function RiskCheckerCard() {
  const [selected, setSelected] = useState([]);
  const [riskScore, setRiskScore] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const [autoMode, setAutoMode] = useState(true);
  const intervalRef = useRef(null);
  const animRef = useRef(null);

  // Auto-play animation
  useEffect(() => {
    if (!autoMode) return;
    const sequence = [
      { delay: 800, ids: ['late'] },
      { delay: 2200, ids: ['late', 'contracts'] },
      { delay: 3600, ids: ['late', 'contracts', 'history'] },
      { delay: 6000, ids: [] },
    ];
    const timeouts = sequence.map(({ delay, ids }) =>
      setTimeout(() => setSelected(ids), delay)
    );
    intervalRef.current = setTimeout(() => {
      setSelected([]);
      // Restart
      setAutoMode(false);
      setTimeout(() => setAutoMode(true), 100);
    }, 7500);
    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(intervalRef.current);
    };
  }, [autoMode]);

  // Calculate risk score
  useEffect(() => {
    const total = BEHAVIOURS.filter(b => selected.includes(b.id)).reduce((sum, b) => sum + b.risk, 0);
    setRiskScore(total);
  }, [selected]);

  // Animate score display
  useEffect(() => {
    const start = displayScore;
    const target = riskScore;
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(start + (target - start) * eased));
      if (progress < 1) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [riskScore]);

  const handleToggle = (id, e) => {
    e.preventDefault();
    setAutoMode(false);
    clearTimeout(intervalRef.current);
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const risk = getRiskLevel(displayScore);

  return (
    <Link to="/protection-hub" className="block sm:col-span-2 lg:col-span-1" data-testid="feature-card-protection">
      <div className="h-full bg-[#0c0c12] border border-white/[0.06] rounded-xl p-5 sm:p-6 transition-all duration-300 hover:border-[#FF4500]/30 hover:shadow-[0_0_30px_-12px_rgba(255,69,0,0.15)] flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#FF4500]/10 flex items-center justify-center shrink-0">
            <Shield className="w-4 h-4 text-[#FF4500]" />
          </div>
          <h3 className="font-clash text-lg font-medium text-white">Client Risk Checker</h3>
        </div>

        {/* Interactive area */}
        <div className="bg-black/40 border border-white/[0.06] rounded-lg p-4 flex-1">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <p className="text-[11px] text-white/60">Select client behaviours</p>
          </div>

          {/* Behaviour tags */}
          <div className="flex flex-col gap-2 mb-5">
            {BEHAVIOURS.map(b => (
              <button
                key={b.id}
                onClick={(e) => handleToggle(b.id, e)}
                className={`w-full text-left px-3 py-2 rounded-lg text-[11px] font-medium transition-all duration-300 border ${
                  selected.includes(b.id)
                    ? 'bg-[#FF4500]/15 border-[#FF4500]/40 text-white shadow-[0_0_10px_-3px_rgba(255,69,0,0.3)]'
                    : 'bg-white/[0.03] border-white/[0.06] text-white/50 hover:border-white/[0.12]'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    selected.includes(b.id) ? 'bg-[#FF4500]' : 'bg-white/20'
                  }`} />
                  {b.label}
                </span>
              </button>
            ))}
          </div>

          {/* Risk meter */}
          <div className="mb-3">
            <div className="flex justify-between text-[9px] text-white/30 mb-1.5">
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
            </div>
            <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${Math.min(displayScore, 100)}%`,
                  background: `linear-gradient(90deg, #22c55e 0%, #f59e0b 50%, #ef4444 100%)`,
                }}
              />
            </div>
          </div>

          {/* Result */}
          <div className={`text-center py-2 rounded-lg transition-all duration-500 ${
            displayScore >= 70 ? 'bg-red-500/10' : displayScore >= 35 ? 'bg-amber-500/10' : displayScore > 0 ? 'bg-green-500/10' : 'bg-white/[0.02]'
          }`}>
            <p className={`text-sm font-bold transition-all duration-300 ${
              displayScore >= 70 ? 'text-red-400 animate-pulse' : displayScore >= 35 ? 'text-amber-400' : displayScore > 0 ? 'text-green-400' : 'text-white/30'
            }`}>
              {risk.label}
            </p>
          </div>
        </div>

        <p className="text-xs text-white/30 italic mt-4 mb-3">Don't guess — know the risk</p>
        <span className="inline-flex items-center text-sm font-medium text-[#FF4500] gap-1.5 group">
          Check Client Risk <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}
